import { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as Dot } from "../../../../assets/dot-primary.svg";
import { ReactComponent as RightArrow } from "../../../../assets/landing-arrow-right.svg";
import {
  PricingViewModel,
  PricingTierViewModel,
} from "../../../../utils/types/PricingViewModels";

interface PricingTableProps {
  viewModel: PricingViewModel;
  audience: "b2b" | "b2c";
}

const PricingTable: FunctionComponent<PricingTableProps> = ({
  audience,
  viewModel,
}) => {
  if (viewModel.loading) return <LoadingState />;
  if (viewModel.error) return <ErrorState error={viewModel.error} />;
  if (viewModel.tiers.length === 0) return <EmptyState />;

  const sortedTiers = viewModel.tiers;
  const allFeatures =
    audience === "b2b"
      ? [
          "Closed Client Referral Loop",
          "Access to ClientVitals Credit Report Packages",
          "5 Free Report Packages Per Month",
          "10 Free Report Packages Per Month",
          "Promotion on Trusted Partner page",
        ]
      : [
          "Discounted Intake & Assessment",
          "1 Report Package Per Month",
          "1 ClientVitals Action Plan per Month",
          "Intake & Assessment Appointment",
          "Follow-Up Appointment",
        ];

  const prices =
    audience === "b2b"
      ? [
          "$5 per Month",
          "$99 per Month",
          "$249 per Month",
          "$399 per Month",
          "Contact For Pricing",
        ]
      : ["$5 per Month", "$50 per Month", "$150 per Month"];

  return (
    <div className="px-8 py-6 mb-10">
      <PricingIntroduction audience={audience} />

      {/* Desktop Table */}
      <div className="hidden lg:block">
        <table className="w-full border-collapse border border-black">
          <TableHeader tiers={sortedTiers} />
          <TableBody
            tiers={sortedTiers}
            features={allFeatures}
            prices={prices}
          />
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden grid grid-cols-1 gap-y-6">
        {sortedTiers.map((tier) => (
          <MobileTierCard key={tier.id} tier={tier} />
        ))}
      </div>
    </div>
  );
};

const PricingIntroduction: FunctionComponent<{ audience: "b2b" | "b2c" }> = ({
  audience,
}) => (
  <div className="my-14">
    <h4 className="text-2xl font-medium text-center mb-3 ">
      Subscription Tiers
    </h4>
    <p className="text-center">
      {audience === "b2b"
        ? "ClientVitals offers multiple plans, each carefully designed to address the unique needs of lenders, financial professionals, and individuals."
        : "ClientVitals offers multiple plans, each carefully designed to address the unique needs related to your financial goals"}
    </p>
  </div>
);

const TableHeader: FunctionComponent<{
  tiers: ReadonlyArray<PricingTierViewModel>;
}> = ({ tiers }) => (
  <thead>
    <tr>
      <th className="border border-black p-4 bg-gray-50 text-left w-1/6"></th>
      {tiers.map((tier) => (
        <th
          key={tier.id}
          className="border border-black p-4 bg-gray-50 text-center text-xl font-medium"
        >
          {tier.name}
        </th>
      ))}
    </tr>
  </thead>
);

const TableBody: FunctionComponent<{
  tiers: ReadonlyArray<PricingTierViewModel>;
  features: string[];
  prices: string[];
}> = ({ tiers, features, prices }) => (
  <tbody>
    {/* Best For Row */}
    <tr>
      <td className="border border-black p-4 font-medium bg-gray-50">
        Best For...
      </td>
      {tiers.map((tier) => (
        <td key={tier.id} className="border border-black p-4 text-sm">
          {tier.description}
        </td>
      ))}
    </tr>

    {/* Feature Rows */}
    {features.map((feature) => (
      <FeatureRow key={feature} feature={feature} tiers={tiers} />
    ))}

    <tr>
      <td className="border border-black p-4 font-medium bg-gray-50">Price</td>
      {prices.map((price) => (
        <td
          key={price}
          className="border border-black p-4 text-lg text-center font-semibold"
        >
          {price}
        </td>
      ))}
    </tr>

    {/* Action Buttons Row */}
    <tr>
      <td className="border border-black p-4"></td>
      {tiers.map((tier) => (
        <td key={tier.id} className="border border-black p-4 text-center">
          <ActionButton tier={tier} />
        </td>
      ))}
    </tr>
  </tbody>
);

const FeatureRow: FunctionComponent<{
  feature: string;
  tiers: ReadonlyArray<PricingTierViewModel>;
}> = ({ feature, tiers }) => (
  <tr>
    <td className="border border-black p-4 font-medium bg-gray-50">
      {feature}
    </td>
    {tiers.map((tier) => (
      <td key={tier.id} className="border border-black p-4 text-center">
        <div className="flex items-center justify-center">
          {tier.features.includes(feature) && <Dot height={16} width={16} />}
        </div>
      </td>
    ))}
  </tr>
);

const ActionButton: FunctionComponent<{ tier: PricingTierViewModel }> = ({
  tier,
}) => (
  <Link
    to={tier.actionUrl}
    className="inline-flex items-center justify-center text-primary uppercase hover:underline font-medium"
  >
    {tier.actionText}
    <RightArrow height={16} width={20} className="ml-2" />
  </Link>
);

const MobileTierCard: FunctionComponent<{ tier: PricingTierViewModel }> = ({
  tier,
}) => (
  <div className="border border-black">
    <div className="border-b border-black p-4 text-center bg-gray-50">
      <h3 className="text-xl font-medium">{tier.name}</h3>
    </div>
    <div className="border-b border-black p-4">
      <p className="text-sm font-medium mb-2">Best For:</p>
      <p className="text-sm">{tier.description}</p>
    </div>
    <div className="border-b border-black p-4">
      {tier.features.map((feature: string, index: number) => (
        <div key={index} className="flex items-center mb-2 last:mb-0">
          <Dot height={12} width={12} className="mr-3 flex-shrink-0" />
          <p className="text-sm">{feature}</p>
        </div>
      ))}
    </div>
    <div className="p-4 text-center">
      <ActionButton tier={tier} />
    </div>
  </div>
);

const LoadingState: FunctionComponent = () => (
  <div className="flex justify-center items-center py-16">
    <p className="text-lg">Loading pricing information...</p>
  </div>
);

const ErrorState: FunctionComponent<{ error: string }> = ({ error }) => (
  <div className="text-center py-16">
    <p className="text-red-600 text-lg mb-4">Error loading pricing</p>
    <p className="text-gray-600">{error}</p>
  </div>
);

const EmptyState: FunctionComponent = () => (
  <div className="text-center py-16">
    <p className="text-gray-600 text-lg">No pricing tiers available</p>
  </div>
);

export default PricingTable;
